import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils';
import { getCurrentLang } from '../../i18n/i18n';
import AlreadyPaidRow from './AlreadyPaidRow';
import { ReservationSummaryState } from './useReservationSummary';
import CoveredAmountNote from './CoveredAmountNote';
import type { CoveredBy } from '../../utils/bookingCoverage';

interface AlreadyPaidSectionProps {
    summary: ReservationSummaryState;
    amountPaid: number;
    /** Inline-editable "mark as paid" control (edit surface) vs a read-only line. */
    editable: boolean;
    /** Covered by an organization: no "Fälliger Betrag" — nothing is due from
     *  the customer — but a sentence naming who bears the cost. */
    coveredBy?: CoveredBy | null;
}

/**
 * Shared "already paid" + "amount due" rows for the reservation price boxes
 * (venue / room / non-room "twins"). `editable` shows the inline-edit
 * {@link AlreadyPaidRow}; otherwise a read-only already-paid line. The amount-due
 * row always follows. Extracted to remove the copy-paste across the three boxes
 * (no-duplication rule). Callers gate on `amountPaid !== undefined` (and the
 * price preview being ready).
 */
function AlreadyPaidSection({ summary, amountPaid, editable, coveredBy = null }: Readonly<AlreadyPaidSectionProps>) {
    const { t } = useTranslation();
    return (
        <>
            {editable ? (
                <AlreadyPaidRow summary={summary} amountPaid={amountPaid} />
            ) : (
                <div className={'flex justify-between text-sm mt-4'}>
                    <span>{t('booking_details.reservation_summary.already_paid')}</span>
                    <span>{formatCurrency(amountPaid, getCurrentLang())}</span>
                </div>
            )}
            {coveredBy ? <CoveredAmountNote coveredBy={coveredBy} /> : (<div className={'flex justify-between text-sm mt-4'}>
                <span>{t('booking_details.reservation_summary.amount_due')}</span>
                {/* formatCurrency shows the magnitude; prefix a minus so an
                    overpayment (negative due, e.g. after a discount below what's
                    already paid) reads as a refund instead of appearing positive. */}
                <span className={'font-bold'}>{summary.amountDue < 0 ? '-' : ''}{formatCurrency(summary.amountDue, getCurrentLang())}</span>
            </div>)}
        </>
    );
}

export default AlreadyPaidSection;
